import type { MarketingPixelsConfig } from "../../../services/marketingPixels";

type PublicFunnelEventName =
  | "page_view"
  | "service_selected"
  | "slot_selected"
  | "form_started"
  | "checkout_started"
  | "appointment_created"
  // OFR-19: so vai para o funil do Agiliza. Nao vira evento de pixel: cai no
  // `default` de `mapPixelEventNames`, sem nome no Meta nem no GA4.
  | "offer_opened";

export type PublicPixelFunnelPayload = {
  eventName: PublicFunnelEventName;
  sessionId?: string;
  serviceId?: string | null;
  providerId?: string | null;
  scheduledDate?: string | null;
  scheduledTime?: string | null;
  bookingSource?: string;
  bookingSourceDetail?: string | null;
  metadata?: Record<string, unknown>;
};

const CUSTOMER_PII_KEYS = new Set([
  "name",
  "customerName",
  "email",
  "customerEmail",
  "phone",
  "customerPhone",
  "whatsapp",
  "customerWhatsapp",
  "cpf",
  "notes",
]);

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    __agilizaPixelTrackingReady?: boolean;
    __agilizaPublicFunnelQueue?: Array<{
      slug: string;
      payload: PublicPixelFunnelPayload;
    }>;
    __agilizaPixelTracking?: {
      cleanup?: () => void;
      metaPixelIds: Set<string>;
      googleTagIds: Set<string>;
    };
  }
}

function hasConfig(config: MarketingPixelsConfig | null | undefined): boolean {
  return Boolean(
    (config?.meta.enabled && config.meta.pixelId) ||
    (config?.ga4.enabled && config.ga4.measurementId) ||
    (config?.googleAds.enabled && config.googleAds.conversionId)
  );
}

function ensureState() {
  if (!window.__agilizaPixelTracking) {
    window.__agilizaPixelTracking = {
      metaPixelIds: new Set<string>(),
      googleTagIds: new Set<string>(),
    };
  }
  return window.__agilizaPixelTracking;
}

function appendScriptOnce(id: string, src: string) {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function initMeta(pixelId: string) {
  const state = ensureState();
  if (!pixelId || state.metaPixelIds.has(pixelId)) return;

  if (!window.fbq) {
    const fbq = function (...args: any[]) {
      (fbq as any).callMethod
        ? (fbq as any).callMethod(...args)
        : (fbq as any).queue.push(args);
    } as any;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
  }

  const fbq = window.fbq;
  if (!fbq) return;
  fbq("init", pixelId);
  state.metaPixelIds.add(pixelId);
  appendScriptOnce(
    "agiliza-meta-pixel",
    "https://connect.facebook.net/en_US/fbevents.js"
  );
}

function initGoogle(tagId: string) {
  const state = ensureState();
  if (!tagId || state.googleTagIds.has(tagId)) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: any[]) {
      window.dataLayer?.push(args);
    };
  window.gtag("js", new Date());
  window.gtag("config", tagId);
  state.googleTagIds.add(tagId);
  appendScriptOnce(
    `agiliza-google-tag-${tagId.replace(/[^a-z0-9_-]/gi, "-")}`,
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`
  );
}

function centsToUnitValue(value: unknown): number | undefined {
  const cents = Number(value || 0);
  if (!Number.isFinite(cents) || cents <= 0) return undefined;
  return Math.round(cents) / 100;
}

function stripPii(
  metadata: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!metadata) return {};
  return Object.entries(metadata).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (CUSTOMER_PII_KEYS.has(key)) return acc;
    if (Array.isArray(value)) {
      acc[key] = value.map((item) =>
        typeof item === "object" && item !== null
          ? stripPii(item as Record<string, unknown>)
          : item
      );
      return acc;
    }
    if (typeof value === "object" && value !== null) {
      acc[key] = stripPii(value as Record<string, unknown>);
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});
}

export function buildPixelEventPayload(payload: PublicPixelFunnelPayload) {
  const metadata = stripPii(payload.metadata);
  const contentIds = Array.isArray(metadata.selectedServiceIds)
    ? metadata.selectedServiceIds
    : payload.serviceId
      ? [payload.serviceId]
      : undefined;
  const value =
    centsToUnitValue(metadata.amountCents) ??
    centsToUnitValue(metadata.valueCents) ??
    centsToUnitValue(metadata.priceCents);

  return {
    currency: "BRL",
    ...(value ? { value } : {}),
    ...(payload.serviceId ? { content_ids: contentIds, content_type: "service" } : {}),
    ...(metadata.serviceName ? { content_name: metadata.serviceName } : {}),
    ...(payload.providerId ? { provider_id: payload.providerId } : {}),
    ...(payload.sessionId ? { session_id: payload.sessionId } : {}),
    ...(payload.bookingSource ? { booking_source: payload.bookingSource } : {}),
    ...(payload.bookingSourceDetail
      ? { booking_source_detail: payload.bookingSourceDetail }
      : {}),
    ...(metadata.appointmentId ? { appointment_id: metadata.appointmentId } : {}),
    ...(payload.scheduledDate ? { scheduled_date: payload.scheduledDate } : {}),
    ...(payload.scheduledTime ? { scheduled_time: payload.scheduledTime } : {}),
  };
}

export function mapPixelEventNames(eventName: PublicFunnelEventName) {
  switch (eventName) {
    case "page_view":
      return { meta: ["PageView"], ga4: "page_view", googleAdsConversion: false };
    case "service_selected":
      return { meta: ["ViewContent"], ga4: "select_item", googleAdsConversion: false };
    case "slot_selected":
      return {
        meta: ["AgilizaSlotSelected"],
        ga4: "select_appointment_slot",
        googleAdsConversion: false,
      };
    case "form_started":
      return {
        meta: ["AgilizaFormStarted"],
        ga4: "form_start",
        googleAdsConversion: false,
      };
    case "checkout_started":
      return {
        meta: ["InitiateCheckout"],
        ga4: "begin_checkout",
        googleAdsConversion: false,
      };
    case "appointment_created":
      return {
        meta: ["Schedule", "Lead", "AgilizaAppointmentConfirmed"],
        ga4: "generate_lead",
        googleAdsConversion: true,
      };
    default:
      return { meta: [], ga4: null, googleAdsConversion: false };
  }
}

export function dispatchPixelEvent(
  config: MarketingPixelsConfig | null | undefined,
  payload: PublicPixelFunnelPayload
) {
  if (typeof window === "undefined" || !hasConfig(config)) return;
  const eventNames = mapPixelEventNames(payload.eventName);
  const eventPayload = buildPixelEventPayload(payload);

  try {
    if (config?.meta.enabled && config.meta.pixelId && window.fbq) {
      for (const name of eventNames.meta) {
        const method = name.startsWith("Agiliza") ? "trackCustom" : "track";
        window.fbq(method, name, eventPayload);
      }
    }

    if (
      config?.ga4.enabled &&
      config.ga4.measurementId &&
      eventNames.ga4 &&
      window.gtag
    ) {
      window.gtag("event", eventNames.ga4, {
        send_to: config.ga4.measurementId,
        ...eventPayload,
      });
    }

    if (
      config?.googleAds.enabled &&
      config.googleAds.conversionId &&
      config.googleAds.conversionLabel &&
      eventNames.googleAdsConversion &&
      window.gtag
    ) {
      window.gtag("event", "conversion", {
        send_to: `${config.googleAds.conversionId}/${config.googleAds.conversionLabel}`,
        ...eventPayload,
      });
    }
  } catch {
    // Pixels externos nunca podem quebrar o agendamento.
  }
}

export function startPublicPixelTracking(
  config: MarketingPixelsConfig | null | undefined
): () => void {
  if (typeof window === "undefined" || !hasConfig(config)) return () => undefined;

  try {
    if (config?.meta.enabled && config.meta.pixelId) initMeta(config.meta.pixelId);
    if (config?.ga4.enabled && config.ga4.measurementId) {
      initGoogle(config.ga4.measurementId);
    }
    if (config?.googleAds.enabled && config.googleAds.conversionId) {
      initGoogle(config.googleAds.conversionId);
    }
  } catch {
    return () => undefined;
  }

  const handler = (event: Event) => {
    const detail = (event as CustomEvent).detail || {};
    if (!detail.payload) return;
    dispatchPixelEvent(config, detail.payload);
  };

  window.addEventListener("agiliza:public-funnel-event", handler);
  window.__agilizaPixelTrackingReady = true;

  const queuedEvents = window.__agilizaPublicFunnelQueue || [];
  window.__agilizaPublicFunnelQueue = [];
  for (const queuedEvent of queuedEvents) {
    if (queuedEvent.payload) {
      dispatchPixelEvent(config, queuedEvent.payload);
    }
  }

  return () => {
    window.__agilizaPixelTrackingReady = false;
    window.removeEventListener("agiliza:public-funnel-event", handler);
  };
}
